/**
 * Layout Component - Futuristic AI-Powered Application Shell
 * Version: 6.2.0
 */
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import {
  LayoutDashboard,
  Link2,
  FlaskConical,
  BarChart3,
  Bell,
  Sparkles,
  Activity,
  Code,
  Settings,
  BookOpen,
  Brain,
  Zap,
  Home,
  Search,
  Menu,
  X,
  LogIn,
  UserPlus,
  LogOut,
} from 'lucide-react'
import NavigationGuide from './NavigationGuide'
import ThemeSelector from './ThemeSelector'
import { MobileNav, MobileBottomSheet } from './MobileNav'
import { FeatureTooltip, FeatureDiscovery, KeyboardShortcuts } from './FeatureTooltip'
import OnboardingWizard from './OnboardingWizard'
import SupportWidget from './SupportWidget'
import './Layout.css'

// Feature info for tooltips
const NAV_TOOLTIPS = {
  '/': { title: 'Dashboard', description: 'View your trading dashboard with real-time metrics', shortcut: 'G then D' },
  '/brokers': { title: 'Broker Connect', description: 'Connect to 82+ broker adapters', shortcut: 'G then B' },
  '/strategies': { title: 'Strategy Lab', description: 'Build and test trading strategies', shortcut: 'G then S' },
  '/analytics': { title: 'Analytics', description: 'View performance reports and analytics', shortcut: 'Ctrl+K' },
  '/notifications': { title: 'Notifications', description: 'Manage alerts and notifications', shortcut: 'Ctrl+K' },
}

function Layout() {
  const location = useLocation()
  const navigate = useNavigate()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [navGuideOpen, setNavGuideOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [featureDiscoveryOpen, setFeatureDiscoveryOpen] = useState(false)
  const [showReplayConfirm, setShowReplayConfirm] = useState(false)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  useEffect(() => {
    // Check authentication status
    const token = localStorage.getItem('auth_token')
    setIsAuthenticated(!!token)
  }, [])

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleLogout = () => {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user_id')
    localStorage.removeItem('username')
    localStorage.removeItem('user_role')
    setIsAuthenticated(false)
    navigate('/landing')
  }

  // Keyboard shortcut for navigation guide (Ctrl+K or Ctrl+/)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === '/')) {
        e.preventDefault()
        setNavGuideOpen(true)
      }
      if (e.key === 'Escape') {
        setNavGuideOpen(false)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const navItems = [
    { path: '/', label: 'Neural Dashboard', icon: Brain, description: 'AI-powered insights', tooltip: NAV_TOOLTIPS['/'] },
    { path: '/brokers', label: 'Broker Connect', icon: Link2, description: '82 adapters ready', tooltip: NAV_TOOLTIPS['/brokers'] },
    { path: '/strategies', label: 'Strategy Lab', icon: FlaskConical, description: 'Build & backtest', tooltip: NAV_TOOLTIPS['/strategies'] },
    { path: '/marketplace', label: 'Marketplace', icon: Sparkles, description: 'Strategy marketplace', tooltip: { title: 'Marketplace', description: 'Discover & clone strategies', shortcut: 'G then M' } },
    { path: '/analytics', label: 'Analytics', icon: BarChart3, description: 'Trading insights', tooltip: NAV_TOOLTIPS['/analytics'] },
    { path: '/notifications', label: 'Notifications', icon: Bell, description: 'Alerts & updates', tooltip: NAV_TOOLTIPS['/notifications'] },
    { path: '/tutorials', label: 'Tutorials', icon: BookOpen, description: 'Video guides', tooltip: { title: 'Tutorials', description: 'Learn how to use ZO Bull', shortcut: 'G then T' } },
    { path: '/options-analysis', label: 'Options Analysis', icon: Activity, description: 'PCR, Greeks, IV', tooltip: { title: 'Options Analysis', description: 'Analyze options metrics', shortcut: 'G then O' } },
    { path: '/pine-script-editor', label: 'Pine Script', icon: Code, description: 'Write strategies', tooltip: { title: 'Pine Script Editor', description: 'Write and test Pine Script', shortcut: 'G then P' } },
    { path: '/tradetron-settings', label: 'TradetrON', icon: Zap, description: 'Integration settings', tooltip: { title: 'TradetrON Settings', description: 'Configure TradetrON integration', shortcut: 'G then T' } },
  ]

  const isActive = (path: string) => location.pathname === path

  // Onboarding Replay Functions
  const handleReplayOnboarding = () => {
    setShowReplayConfirm(true)
  }

  const confirmReplayOnboarding = () => {
    // Clear onboarding flags
    localStorage.removeItem('onboarding_completed')
    localStorage.removeItem('onboarding_date')
    localStorage.removeItem('quickstart_completed')
    localStorage.removeItem('quickstart_shown')
    setShowReplayConfirm(false)
    setSettingsOpen(false)
    setShowOnboarding(true)
  }

  const cancelReplayOnboarding = () => {
    setShowReplayConfirm(false)
  }

  const handleOnboardingComplete = () => {
    localStorage.setItem('onboarding_completed', 'true')
    setShowOnboarding(false)
  }

  const handleOnboardingSkip = () => {
    localStorage.setItem('onboarding_completed', 'true')
    setShowOnboarding(false)
  }

  // Check if onboarding should show on mount
  useEffect(() => {
    const onboardingCompleted = localStorage.getItem('onboarding_completed')
    if (!onboardingCompleted) {
      setShowOnboarding(true)
    }
  }, [])

  return (
    <div className="layout">
      {/* Animated Background Elements */}
      <div className="bg-orb bg-orb-1" />
      <div className="bg-orb bg-orb-2" />
      <div className="bg-orb bg-orb-3" />

      {/* AI Status Indicator */}
      <div className="ai-status">
        <span className="ai-dot" />
        <span>AI Engine Active</span>
      </div>

      {/* Mobile Menu Button */}
      <button 
        className="mobile-menu-btn"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label="Toggle menu"
      >
        <span className="hamburger-line" />
        <span className="hamburger-line" />
        <span className="hamburger-line" />
      </button>

      {/* Mobile Navigation Overlay */}
      {mobileMenuOpen && (
        <div className="mobile-nav-overlay" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* Mobile Navigation */}
      <nav className={`mobile-nav ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-nav-header">
          <h3>ZO BULL</h3>
          <span className="ai-badge">AI Powered</span>
        </div>
        <button className="mobile-nav-close" onClick={() => setMobileMenuOpen(false)}>
          <X size={20} />
        </button>
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`mobile-nav-item ${isActive(item.path) ? 'active' : ''}`}
            onClick={() => setMobileMenuOpen(false)}
          >
            <span className="nav-icon"><item.icon size={24} /></span>
            <div className="nav-text">
              <span className="nav-label">{item.label}</span>
              <span className="nav-desc">{item.description}</span>
            </div>
          </Link>
        ))}
        <div className="mobile-nav-footer">
          <button 
            className="mobile-nav-guide-btn"
            onClick={() => {
              setMobileMenuOpen(false)
              setNavGuideOpen(true)
            }}
          >
            <Search size={18} />
            <span>Navigation Guide</span>
          </button>
        </div>
      </nav>

      {/* Desktop Header */}
      <header className={`header ${scrolled ? 'scrolled' : ''}`}>
        <div className="header-content">
          <div className="header-left">
            <div className="logo">
              <img src="/logo.png" alt="ZO Bull Logo" className="logo-image" />
              <div className="logo-text">
                <h1>ZO BULL</h1>
                <span className="logo-subtitle">Algo Trading Platform</span>
              </div>
            </div>
            <div className="header-badge">
              <span className="ai-badge">AI Powered</span>
            </div>
          </div>
          <div className="header-right">
            {isAuthenticated ? (
              <>
                <button 
                  className="header-auth-btn logout-btn"
                  onClick={handleLogout}
                  title="Logout"
                >
                  <LogOut size={18} />
                  <span className="btn-text">Logout</span>
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="header-auth-btn login-btn">
                  <LogIn size={18} />
                  <span className="btn-text">Login</span>
                </Link>
                <Link to="/register" className="header-auth-btn signup-btn">
                  <UserPlus size={18} />
                  <span className="btn-text">Sign Up</span>
                </Link>
              </>
            )}
            <ThemeSelector />
            <div className="live-indicator">
              <span className="pulse-ring" />
              <span className="pulse-dot" />
              <span>LIVE</span>
            </div>
            <button 
              className="nav-guide-btn"
              onClick={() => setNavGuideOpen(true)}
              title="Navigation Guide (Ctrl+K)"
            >
              <Search size={18} />
              <span className="btn-text">Guide</span>
              <kbd>⌘K</kbd>
            </button>
          </div>
        </div>
      </header>

      {/* Breadcrumbs */}
      <div className="breadcrumbs">
        <a href="/" className="breadcrumb-item">Home</a>
        {location.pathname !== '/' && (
          <>
            <span className="breadcrumb-sep">/</span>
            <span className="breadcrumb-item active">
              {navItems.find(item => item.path === location.pathname)?.label || 'Page'}
            </span>
          </>
        )}
      </div>

      {/* Desktop Navigation */}
      <nav className={`desktop-nav ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          {navItems.map((item, index) => (
            <FeatureTooltip
              key={item.path}
              feature={item.tooltip!}
              position="bottom"
              delay={500}
            >
              <Link
                to={item.path}
                className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <span className="nav-icon"><item.icon size={20} /></span>
                <div className="nav-text">
                  <span className="nav-label">{item.label}</span>
                  <span className="nav-desc">{item.description}</span>
                </div>
                {isActive(item.path) && <span className="nav-indicator" />}
              </Link>
            </FeatureTooltip>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <main className="main-content">
        <Outlet />
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileNav onOpenSettings={() => setSettingsOpen(true)} />

      {/* Mobile Settings Bottom Sheet */}
      <MobileBottomSheet 
        isOpen={settingsOpen} 
        onClose={() => setSettingsOpen(false)}
        title="Settings"
      >
        <div className="settings-list">
          <div className="settings-item">
            <span>🔔</span>
            <span>Notifications</span>
            <span>→</span>
          </div>
          <div className="settings-item theme-selector-trigger" onClick={() => {}}>
            <span>🎨</span>
            <span>Theme</span>
            <ThemeSelector className="settings-theme-selector" />
          </div>
          <div className="settings-item" onClick={handleReplayOnboarding}>
            <span>🔄</span>
            <span>Replay Setup Wizard</span>
            <span className="settings-arrow">→</span>
          </div>
          <div className="settings-item">
            <span>🔒</span>
            <span>Security</span>
            <span>→</span>
          </div>
          <div className="settings-item">
            <span>❓</span>
            <span>Help & Support</span>
            <span>→</span>
          </div>
        </div>
      </MobileBottomSheet>

      {/* Onboarding Replay Confirmation */}
      {showReplayConfirm && (
        <div className="replay-confirm-overlay" onClick={cancelReplayOnboarding}>
          <div className="replay-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="replay-confirm-icon">🔄</div>
            <h3>Replay Setup Wizard?</h3>
            <p>This will reset your setup progress and show the onboarding wizard again.</p>
            <div className="replay-confirm-actions">
              <button className="btn-cancel" onClick={cancelReplayOnboarding}>
                Cancel
              </button>
              <button className="btn-confirm" onClick={confirmReplayOnboarding}>
                Yes, Replay
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Guide */}
      <NavigationGuide isOpen={navGuideOpen} onClose={() => setNavGuideOpen(false)} />

      {/* Feature Discovery Modal */}
      <FeatureDiscovery isOpen={featureDiscoveryOpen} onClose={() => setFeatureDiscoveryOpen(false)} />

      {/* Onboarding Wizard */}
      {showOnboarding && (
        <OnboardingWizard 
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
        />
      )}

      {/* Support Widget - Tawk.to Style */}
      <SupportWidget position="bottom-right" />

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-left">
            <span className="footer-logo">ZO BULL</span>
            <span className="footer-version">v6.2.0</span>
          </div>
          <div className="footer-center">
            <button 
              className="discover-btn"
              onClick={() => setFeatureDiscoveryOpen(true)}
            >
              ✨ Discover Features
            </button>
          </div>
          <div className="footer-right">
            <span>Press <kbd>Ctrl</kbd>+<kbd>K</kbd> for navigation</span>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default Layout